import { reportError } from './reportError';

/**
 * Central error map: stable code → user-facing copy.
 *
 * Every error shown to the user should resolve to one of these codes so
 * (a) the UI never shows raw server/transport text ("Request failed (502)",
 * "Invalid or expired authentication token"), and (b) the same code travels
 * to the error-reporting-service, making UI errors greppable in ops-dashboard.
 *
 * UNKNOWN is deliberately absent from the copy map: an unclassified error
 * keeps its (cleaned) original message, because most call sites already pass
 * intentional copy ("Failed to fetch likes") that must not be clobbered.
 */
export const ERROR_CODES = {
    AUTH_EXPIRED: 'Your session expired — please try again in a moment while we reconnect you.',
    AUTH_REQUIRED: 'Please sign in to continue.',
    SERVER_UNAVAILABLE: 'MixGoat servers are temporarily unavailable — please try again in a moment.',
    SERVER_ERROR: 'Something went wrong on our side — please try again.',
    NETWORK_OFFLINE: "Can't reach MixGoat — check your internet connection.",
    TIMEOUT: 'The request took too long — please try again.',
    RATE_LIMITED: 'Too many requests — please wait a moment and try again.',
    NOT_FOUND: "That content couldn't be found.",
};

const GENERIC_MESSAGE = 'Something went wrong — please try again.';

// Whether an immediate manual retry can plausibly fix the error. Drives the
// ErrorBanner Retry button: AUTH_* self-heal via the token renewal/reconnect
// loops (a manual retry just races them), RATE_LIMITED gets WORSE if
// hammered, NOT_FOUND won't start existing. UNKNOWN gets the benefit of the
// doubt — its call sites usually say "please try again" themselves.
const RETRYABLE = {
    NETWORK_OFFLINE: true,
    TIMEOUT: true,
    SERVER_UNAVAILABLE: true,
    SERVER_ERROR: true,
    UNKNOWN: true,
    AUTH_EXPIRED: false,
    AUTH_REQUIRED: false,
    RATE_LIMITED: false,
    NOT_FOUND: false,
};

export const isRetryable = (code) => RETRYABLE[code] !== false;

// libraryError() codes (main.js) → our codes. ALREADY_EXISTS is omitted on
// purpose: its server message ("Playlist already exists") is already the
// right thing to show.
const LIBRARY_CODE_MAP = {
    NOT_AUTHENTICATED: 'AUTH_EXPIRED',
    NETWORK_ERROR: 'NETWORK_OFFLINE',
    RATE_LIMITED: 'RATE_LIMITED',
    NOT_FOUND: 'NOT_FOUND',
    SERVER_ERROR: 'SERVER_ERROR',
};

const codeFromStatus = (status) => {
    if (status === 401 || status === 403) return 'AUTH_EXPIRED';
    if (status === 404) return 'NOT_FOUND';
    if (status === 408) return 'TIMEOUT';
    if (status === 429) return 'RATE_LIMITED';
    if (status === 502 || status === 503 || status === 504) return 'SERVER_UNAVAILABLE';
    if (status >= 500) return 'SERVER_ERROR';
    return null;
};

// Ordered: first match wins. Auth before generic 4xx text; gateway phrases
// before the bare internal-server-error phrase.
const MESSAGE_PATTERNS = [
    [/invalid or expired authentication token|authentication token is required|access token required|token (is )?expired|session expired|not authenticated|unauthorized/i, 'AUTH_EXPIRED'],
    [/bad gateway|service unavailable|gateway time-?out/i, 'SERVER_UNAVAILABLE'],
    [/internal server error/i, 'SERVER_ERROR'],
    [/econnaborted|etimedout|timed out/i, 'TIMEOUT'],
    [/econnrefused|enotfound|err_network|err_internet_disconnected|network ?error|failed to fetch|fetch failed|load failed/i, 'NETWORK_OFFLINE'],
    [/too many requests|rate limit/i, 'RATE_LIMITED'],
];

// The status embedded in transport fallback strings: web shim's
// "Request failed (502)", main.js's "HTTP 502" / "(status 502)".
const statusFromMessage = (msg) => {
    const m = msg.match(/request failed \((\d{3})\)/i)
        || msg.match(/^HTTP (\d{3})$/i)
        || msg.match(/\(?status:? (\d{3})\)?/i);
    return m ? Number(m[1]) : null;
};

// A message errMsg/presentError already produced ends with "(CODE)" — don't
// re-classify copy, just recover the code.
const alreadyClassified = (msg) => {
    const m = msg.match(/\(([A-Z][A-Z_]+)\)$/);
    return m && ERROR_CODES[m[1]] ? m[1] : null;
};

const cleanMessage = (raw) => {
    let msg = raw
        // Electron's wrapper around errors thrown across ipcRenderer.invoke
        .replace(/^Error invoking remote method '[^']+':\s*/, '')
        .replace(/^Error:\s*/, '');
    // Python traceback → keep the final "SomeError: reason" line
    if (msg.includes('Traceback (most recent call last)')) {
        const lines = msg.trim().split('\n').map((l) => l.trim()).filter(Boolean);
        msg = lines[lines.length - 1];
    }
    if (msg.length > 300) msg = `${msg.slice(0, 299)}…`;
    return msg;
};

/**
 * Classify any error the renderer sees into a stable code + display copy.
 *
 * Accepts every shape in circulation: plain string, Error, the IPC shapes
 * { error: string } / { error: { code, message, requestId } }, and the web
 * shim's { error, status }.
 *
 * Returns { code, raw, display, status, requestId }:
 *   code      — key of ERROR_CODES, or 'UNKNOWN'
 *   raw       — cleaned original message (for logs, never for UI)
 *   display   — what to show the user: friendly copy + "(CODE)" suffix for
 *               classified errors, the cleaned raw message otherwise
 *   status    — HTTP status when recoverable, else null
 *   requestId — server correlation id when present, else null
 */
export const classifyError = (e) => {
    const inner = (e && typeof e.error === 'object') ? e.error : null;
    const rawMessage =
        typeof e === 'string' ? e
            : typeof e?.message === 'string' ? e.message
                : typeof e?.error === 'string' ? e.error
                    : typeof inner?.message === 'string' ? inner.message
                        : '';
    const raw = cleanMessage(rawMessage);
    const status = (typeof e?.status === 'number' ? e.status : null)
        ?? (typeof inner?.status === 'number' ? inner.status : null)
        ?? statusFromMessage(raw);
    const requestId = e?.requestId || inner?.requestId || null;
    const structuredCode = e?.code || inner?.code || null;

    let code = alreadyClassified(raw)
        || (structuredCode && LIBRARY_CODE_MAP[structuredCode])
        || (status != null && codeFromStatus(status))
        || null;
    if (!code) {
        for (const [re, c] of MESSAGE_PATTERNS) {
            if (re.test(raw)) { code = c; break; }
        }
        // Node network error codes arrive on Error.code too (ECONNREFUSED…)
        if (!code && typeof structuredCode === 'string') {
            for (const [re, c] of MESSAGE_PATTERNS) {
                if (re.test(structuredCode)) { code = c; break; }
            }
        }
    }

    const display = code
        ? `${ERROR_CODES[code]} (${code})`
        : (raw && raw !== '[object Object]' ? raw : GENERIC_MESSAGE);
    return { code: code || 'UNKNOWN', raw: raw || String(e), display, status, requestId };
};

/**
 * Split an error into render-ready parts for UI that styles the code
 * separately (toast chip, ErrorBanner) instead of the inline "(CODE)" suffix:
 *   { text, code } — code is null for unclassified errors.
 * Accepts anything classifyError accepts, including a display string that
 * already carries the "(CODE)" suffix (the suffix is removed from text).
 */
export const displayParts = (e) => {
    const c = classifyError(e);
    if (c.code === 'UNKNOWN') return { text: c.display, code: null };
    return { text: ERROR_CODES[c.code], code: c.code };
};

// One report per code+message per window — a retry loop or a thunk that both
// reports and toasts must not flood the reporting service.
const REPORT_DEDUPE_MS = 30_000;
const recentReports = new Map(); // `${code}|${raw}` -> timestamp

/**
 * Fire-and-forget: ship a user-shown error's code + attributes to PostHog
 * (client_error event) and the error-reporting-service, so every ugly thing a
 * user saw is visible in ops-dashboard with its code, surface, status and
 * requestId. Never throws, never awaited.
 */
export const reportShownError = (classified, attrs = {}) => {
    try {
        const key = `${classified.code}|${classified.raw}`;
        const now = Date.now();
        for (const [k, ts] of recentReports) {
            if (now - ts > REPORT_DEDUPE_MS) recentReports.delete(k);
        }
        if (recentReports.has(key)) return;
        recentReports.set(key, now);
        reportError({ message: classified.raw }, {
            type: 'user_facing_error',
            code: classified.code,
            surface: attrs.surface || null,
            status: classified.status,
            requestId: classified.requestId,
            ...attrs,
        });
    } catch {
        // reporting must never break the UI path
    }
};

/**
 * The one-liner for call sites: classify, report, return the display string.
 *
 *   setError(presentError(result.error, { surface: 'subscription' }))
 *
 * `fallback` (optional) replaces the display for UNCLASSIFIED errors only —
 * for call sites that used to show a hand-written generic message and should
 * keep doing so rather than exposing a cleaned-but-raw string.
 */
export const presentError = (e, { surface, fallback, ...extra } = {}) => {
    const c = classifyError(e);
    reportShownError(c, { surface, ...extra });
    if (c.code === 'UNKNOWN' && fallback) return fallback;
    return c.display;
};
